import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, ArrowLeft, CheckCircle2, RefreshCw, Rocket, Users } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AuthLayout } from '@/components/auth/AuthLayout';

const RESEND_COOLDOWN_SECONDS = 60;

const SignupConfirmation = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const workspaceName = searchParams.get('workspace') || '';
  const hasInvitation = searchParams.has('invite') && searchParams.has('token');
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const { toast } = useToast();

  usePageTitle(hasInvitation ? `Rejoindre ${workspaceName}` : 'Vérifiez votre email');

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handleResendEmail = async () => {
    if (!email || isResending || cooldownSeconds > 0) return;

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) throw error;

      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      toast({
        title: "Email renvoyé !",
        description: "Vérifiez votre boîte de réception.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de renvoyer l'email. Réessayez plus tard.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const getButtonContent = () => {
    if (isResending) {
      return (
        <>
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          Envoi en cours...
        </>
      );
    }
    if (cooldownSeconds > 0) {
      return (
        <>
          <RefreshCw className="w-4 h-4 mr-2" />
          Renvoyer dans {cooldownSeconds}s
        </>
      );
    }
    return (
      <>
        <RefreshCw className="w-4 h-4 mr-2" />
        Renvoyer l'email
      </>
    );
  };

  return (
    <AuthLayout>
      <div className="text-center space-y-8">
        {/* Animated email icon */}
        <div className="mx-auto w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center animate-wiggle-bounce">
          <Mail className="w-12 h-12 text-primary" />
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-foreground">
            Vérifiez votre email
          </h1>
          <p className="text-muted-foreground">
            Nous avons envoyé un lien de confirmation à
          </p>
          {email && (
            <p className="text-foreground font-medium text-lg">
              {email}
            </p>
          )}
        </div>

        {/* Invitation context banner */}
        {hasInvitation && workspaceName && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-left">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-foreground">
                  Vous rejoignez l'équipe <span className="text-primary">{workspaceName}</span>
                </p>
                <p className="text-muted-foreground mt-1">
                  Une fois votre email confirmé, vous serez automatiquement ajouté(e) à cet espace de travail.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-muted/50 rounded-lg p-4 text-left space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              Cliquez sur le lien dans l'email pour activer votre compte
            </p>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              Pensez à vérifier votre dossier <strong>spam</strong> ou <strong>courrier indésirable</strong>
            </p>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              Le lien expire dans 24 heures
            </p>
          </div>
        </div>

        {/* Next steps preview */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-left">
          <div className="flex items-start gap-3">
            <Rocket className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-foreground">
                {hasInvitation ? 'Prochaine étape' : 'Ensuite, configurez votre espace'}
              </p>
              <p className="text-muted-foreground mt-1">
                {hasInvitation 
                  ? `Après confirmation, vous serez connecté(e) et pourrez commencer à collaborer avec l'équipe ${workspaceName}.`
                  : 'Après confirmation, vous serez guidé pour personnaliser votre profil et votre espace de support.'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleResendEmail}
            disabled={isResending || cooldownSeconds > 0}
          >
            {getButtonContent()}
          </Button>

          <Link
            to="/auth"
            className="flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignupConfirmation;
